// Constants
export const DEFAULT_LANGUAGE = 'pt_BR';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  FOOTER: 'FOOTER',
  BUTTONS: 'BUTTONS',
};
export const BUTTON_TYPES = {
  QUICK_REPLY: 'QUICK_REPLY',
  URL: 'URL',
  PHONE_NUMBER: 'PHONE_NUMBER',
  COPY_CODE: 'COPY_CODE',
  FLOW: 'FLOW',
  ORDER_DETAILS: 'ORDER_DETAILS',
  CATALOG: 'CATALOG',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
export const UPLOAD_CONFIG = {
  IMAGE: { accept: 'image/jpeg,image/jpg,image/png' },
  VIDEO: { accept: 'video/mp4' },
  DOCUMENT: { accept: 'application/pdf' },
};
export const NAMED_PARAMETER_MAX_LENGTH = 20;

// Variable parsing regex factories
const getPositionalVariableRegex = () =>
  /(?:\{{1,2}|\[{1,2}|\({1,2})\s*(\d+)\s*(?:\}{1,2}|\]{1,2}|\){1,2})/g;
const getNamedVariableRegex = () => /\{\{([a-z][a-z0-9_]{0,19})\}\}/g;
const namedVariableNameRegex = /^[a-z][a-z0-9_]{0,19}$/;
const liquidVariableRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
const liquidDefaultFilterRegex = /\|\s*default:\s*(['"])(.*?)\1/;

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

const isBlankValue = value =>
  value === undefined || value === null || `${value}`.trim() === '';

const extractDefaultValue = expression => {
  const match = expression.match(liquidDefaultFilterRegex);
  return match?.[2];
};

const resolveContactValue = (path, sampleContact) => {
  if (!sampleContact) return undefined;

  const normalizedPath = path.trim();

  if (normalizedPath === 'first_name') {
    const explicitFirstName = sampleContact.first_name;
    if (!isBlankValue(explicitFirstName)) return explicitFirstName;

    const fullName = sampleContact.name || '';
    return fullName.trim().split(/\s+/)[0];
  }

  if (
    normalizedPath.startsWith('custom_attributes.') ||
    normalizedPath.startsWith('custom_attribute.')
  ) {
    const customKey = normalizedPath.split('.').slice(1).join('.');
    return (
      sampleContact.custom_attributes?.[customKey] ??
      sampleContact.custom_attribute?.[customKey]
    );
  }

  return sampleContact[normalizedPath];
};

export const renderLiquidContactVariables = (text, sampleContact = null) => {
  if (!text) return text;

  return text.replace(liquidVariableRegex, (match, variableExpression) => {
    const trimmedExpression = variableExpression.trim();
    const sourcePath = trimmedExpression.split('|')[0]?.trim();

    if (!sourcePath?.startsWith('contact.')) {
      return match;
    }

    const resolvedValue = resolveContactValue(
      sourcePath.replace(/^contact\./, ''),
      sampleContact
    );

    if (!isBlankValue(resolvedValue)) {
      return `${resolvedValue}`;
    }

    const fallbackValue = extractDefaultValue(trimmedExpression);
    return fallbackValue ?? match;
  });
};

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (
  templateText,
  processedParams,
  componentTypeOrOptions = 'body'
) => {
  const { componentType, sampleContact } =
    typeof componentTypeOrOptions === 'string'
      ? { componentType: componentTypeOrOptions, sampleContact: null }
      : {
          componentType: componentTypeOrOptions?.componentType || 'body',
          sampleContact: componentTypeOrOptions?.sampleContact || null,
        };

  const renderedText = templateText.replace(
    /{{([^}]+)}}/g,
    (match, variable) => {
      const variableKey = processVariable(variable);
      const replacementValue = processedParams?.[componentType]?.[variableKey];

      if (isBlankValue(replacementValue)) {
        return `{{${variable}}}`;
      }

      return renderLiquidContactVariables(`${replacementValue}`, sampleContact);
    }
  );

  return renderLiquidContactVariables(renderedText, sampleContact);
};

const extractTemplateVariables = text => text?.match(/{{([^}]+)}}/g) || [];
const extractTemplateVariableNames = text =>
  extractTemplateVariables(text).map(variable =>
    processVariable(variable).trim()
  );

export const isValidNamedVariableName = name =>
  namedVariableNameRegex.test(name);

const addTextComponentVariables = (allVariables, componentKey, text) => {
  const matchedVariables = extractTemplateVariables(text);
  if (!matchedVariables.length) return;

  allVariables[componentKey] ||= {};
  matchedVariables.forEach(variable => {
    const key = processVariable(variable);
    allVariables[componentKey][key] = '';
  });
};

export const buildTemplateParameters = (template, hasMediaHeaderValue) => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (headerComponent?.format === 'TEXT') {
    addTextComponentVariables(allVariables, 'header', headerComponent.text);
  }

  if (bodyComponent) {
    addTextComponentVariables(allVariables, 'body', bodyComponent.text);
  }

  if (hasMediaHeaderValue) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_url = '';
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    // For document templates, include media_name field for filename support
    if (headerComponent.format.toLowerCase() === 'document') {
      allVariables.header.media_name = '';
    }
  }

  // Process button variables
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(/{{([^}]+)}}/g) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              index: index.toString(),
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
            };
          }
        }

        // Handle copy code buttons and Brazil payment action buttons.
        if (
          [
            'COPY_CODE',
            'ORDER_DETAILS',
            'PAYMENT_REQUEST',
            'PAYMENT_REQUEST_CTA',
            'PAYMENT_REQUEST_BUTTON',
            'CATALOG',
          ].includes(button.type)
        ) {
          if (!allVariables.buttons) allVariables.buttons = [];
          if (button.type === 'ORDER_DETAILS') {
            allVariables.buttons[index] = {
              type: 'order_details',
              index: index.toString(),
              payment_method: 'pix_dynamic_code',
              pix_code: '',
              pix_merchant_name: '',
              pix_key: '',
              pix_key_type: 'CNPJ',
              boleto_digitable_line: '',
              payment_link_uri: '',
              reference_id: '',
              total_amount: '',
            };
          } else if (button.type.startsWith('PAYMENT_REQUEST')) {
            allVariables.buttons[index] = {
              type: 'payment_request',
              index: index.toString(),
              payment_method: 'pix_dynamic_code',
              pix_code: '',
              pix_merchant_name: '',
              pix_key: '',
              pix_key_type: 'CNPJ',
              boleto_digitable_line: '',
              payment_link_uri: '',
            };
          } else if (button.type === 'CATALOG') {
            allVariables.buttons[index] = {
              type: 'catalog',
              index: index.toString(),
              parameter: '',
              optional: true,
            };
          } else {
            allVariables.buttons[index] = {
              type: 'copy_code',
              index: index.toString(),
              parameter: '',
            };
          }
        }
      });
    }
  });

  return allVariables;
};

export const extractNamedVariables = text => {
  const matches = [...text.matchAll(getNamedVariableRegex())];
  return matches.map(match => match[1]);
};

export const hasPositionalVariables = text => {
  return getPositionalVariableRegex().test(text);
};

export const hasNamedVariables = text => {
  return getNamedVariableRegex().test(text);
};

export const parsePositionalVariables = (
  text,
  maxVariables = Infinity,
  existingExamples = []
) => {
  const examples = [...existingExamples];
  const variableNames = extractTemplateVariableNames(text);
  const invalidPositionalVariable = variableNames.find(
    name => !/^\d+$/.test(name)
  );

  if (invalidPositionalVariable) {
    return {
      processedText: text,
      examples: [],
      error: { key: 'NAMED_IN_POSITIONAL' },
    };
  }

  let count = 0;
  const processedText = text.replace(getPositionalVariableRegex(), () => {
    count += 1;
    if (count > maxVariables) return '';
    return `{{${count}}}`;
  });
  count = Math.min(count, maxVariables);

  if (examples.length > count) {
    examples.splice(count);
  }

  for (let i = examples.length; i < count; i += 1) {
    examples.push('');
  }

  return { processedText, examples, error: {} };
};

export const parseNamedVariables = (text, maxVariables, existingExamples) => {
  if (hasPositionalVariables(text)) {
    return {
      processedText: text,
      examples: [],
      error: { key: 'POSITIONAL_IN_NAMED' },
    };
  }

  const error = {};
  const variableNames = extractTemplateVariableNames(text);
  const invalidVariableName = variableNames.find(
    name => !isValidNamedVariableName(name)
  );

  if (invalidVariableName) {
    error.key = 'INVALID_NAMED_VARIABLE';
    error.data = {
      name: invalidVariableName,
      maxLength: NAMED_PARAMETER_MAX_LENGTH,
    };
  }

  const uniqueNames = new Set(variableNames);

  if (error.key) {
    return { processedText: text, examples: existingExamples, error };
  }

  if (variableNames.length !== uniqueNames.size) {
    error.key = 'DUPLICATE_VARIABLES';
  } else if (variableNames.length > maxVariables) {
    error.key = 'MAX_VARIABLES_EXCEEDED';
    error.data = { count: maxVariables };
  }

  const examples = variableNames.map(param_name => ({
    param_name,
    example:
      existingExamples.find(ex => ex && ex.param_name === param_name)
        ?.example || '',
  }));

  return { processedText: text, examples, error };
};

export const parseTemplateVariables = (
  text,
  parameterType,
  maxVariables,
  existingExamples
) => {
  if (parameterType === 'positional') {
    return parsePositionalVariables(text, maxVariables, existingExamples);
  }
  return parseNamedVariables(text, maxVariables, existingExamples);
};

export const buildExamplePropertyByParameterType = (
  exampleData,
  componentType,
  parameterType
) => {
  const positionalKey = `${componentType}_text`;
  const namedKey = `${componentType}_text_named_params`;

  const hasVariables =
    parameterType === 'positional'
      ? exampleData[positionalKey]?.length > 0
      : exampleData[namedKey]?.length > 0;

  if (!hasVariables) return null;

  if (parameterType === 'positional') {
    if (componentType === 'body') {
      return { [positionalKey]: [exampleData[positionalKey]] };
    }
    return { [positionalKey]: exampleData[positionalKey] };
  }

  return { [namedKey]: exampleData[namedKey] };
};

export const generateTemplateComponents = (templateData, parameterType) => {
  const components = [];
  const { header, body, footer, buttons } = templateData;

  if (header.enabled) {
    let data = {};
    if (header.format === 'TEXT') {
      data = { format: header.format, text: header.text };
      const example = buildExamplePropertyByParameterType(
        header.example,
        'header',
        parameterType
      );
      if (example) {
        data.example = example;
      }
    } else if (MEDIA_FORMATS.includes(header.format)) {
      data = {
        format: header.format,
        media: { blob_id: header.media.blobId },
      };
    } else {
      data = { format: header.format };
    }
    components.push({ type: header.type, ...data });
  }

  const bodyComponent = { type: body.type, text: body.text };
  const example = buildExamplePropertyByParameterType(
    body.example,
    'body',
    parameterType
  );
  if (example) {
    bodyComponent.example = example;
  }
  components.push(bodyComponent);

  if (footer.enabled) {
    components.push({ type: footer.type, text: footer.text });
  }

  if (buttons.length) {
    buttons.forEach(b => {
      delete b.error;
    });
    components.push({
      type: COMPONENT_TYPES.BUTTONS,
      buttons: [...buttons],
    });
  }

  return components;
};

export const validateTemplateData = templateData => {
  const { header, body, footer, buttons } = templateData;

  if (body.error || !body.text) return false;

  if (header.enabled) {
    const invalidHeaderText = header.format === 'TEXT' && !header.text?.length;
    const invalidHeaderMedia =
      MEDIA_FORMATS.includes(header.format) && !header.media?.blobId;

    if (header.error || invalidHeaderText || invalidHeaderMedia) {
      return false;
    }
  }

  if (footer.enabled && !footer.text) return false;

  if (buttons && buttons.length > 0) {
    return buttons.every(b => !b.error);
  }

  return true;
};

export const replaceTemplateVariablesByExamples = ({
  templateText,
  examples,
  parameterType,
}) => {
  if (!templateText) return '';
  const replacements = {};
  if (parameterType === 'named') {
    examples.forEach(item => {
      if (item.example) {
        replacements[item.param_name] = item.example;
      }
    });
  } else {
    examples.forEach((exampleVal, index) => {
      if (exampleVal) {
        replacements[index + 1] = exampleVal;
      }
    });
  }
  return replaceTemplateVariables(templateText, { body: replacements });
};

export const toParserFormat = messageTemplate => {
  if (!messageTemplate) return null;
  return {
    ...messageTemplate,
    components: messageTemplate.content?.components || [],
    category: (messageTemplate.category || '').toUpperCase(),
    status: (messageTemplate.status || '').toUpperCase(),
    namespace: messageTemplate.metadata?.namespace || '',
  };
};
